import { useMemo } from "react";
import type { DrawerRouteContainerProps } from "shared-ui/src/components/detail-drawer/drawer-stack";
import { ReadyDrawerBase } from "./DrawerBaseReadiness";
import { RoutedDrawerLoading } from "./RoutedDrawerLoading";
import { StackedTrackingDrawer } from "./StackedTrackingDrawer";
import { ORDER_DRAWER_SEGMENT } from "./orderDrawerSegments";
import { parseOrderRouteKey, parseScopedRouteId } from "./orderRouteKey";
import {
  findTrackingDrawerProps,
  type TrackingDrawerPushProps,
} from "./trackingDrawerProps";
import { useOrderDrawerData } from "./useOrderDrawerData";
import { useRouteNotFoundToast } from "shared-ui/src/hooks/routedOverlays/useRouteNotFoundToast";

export const TRACKING_NOT_FOUND_MESSAGE =
  "Tracking details aren't available for this shipment.";

const isTrackingDrawerPushProps = (
  value: Record<string, unknown> | undefined,
): value is Record<string, unknown> & TrackingDrawerPushProps =>
  typeof value?.trackingNumber === "string";

/**
 * Routed container for `.../tracking/{orderKey}~{shipmentKey}`. An in-app open reuses the
 * pushed shipment. A deep link or refresh loads the order and finds the shipped or delivered
 * shipment whose tracking number hashes to the shipment key.
 */
export function RoutedTrackingDrawer({
  open,
  onClose,
  onCloseAll,
  zIndex,
  depth,
  routeId,
  levelPath,
  ancestors,
  cachedProps,
  onNotFound,
}: DrawerRouteContainerProps) {
  const pushed = isTrackingDrawerPushProps(cachedProps) ? cachedProps : null;

  const target = useMemo(() => {
    const parts = parseScopedRouteId(routeId);
    const identifier = parts ? parseOrderRouteKey(parts.scope) : undefined;
    return parts && identifier
      ? { orderKey: parts.scope, shipmentKey: parts.id, identifier }
      : undefined;
  }, [routeId]);

  const isCold = pushed == null && target != null;
  const { isLoading, isError, hasData, orderData } = useOrderDrawerData({
    identifier: isCold ? target.identifier : null,
    preloadedData: null,
    enabled: open && isCold,
  });

  const resolved = useMemo(
    () =>
      isCold && orderData
        ? findTrackingDrawerProps(orderData, target.shipmentKey)
        : undefined,
    [isCold, orderData, target],
  );

  // A missing order under its own order drawer is reported by that drawer, which closes both.
  const isOrderOwnedByAncestor = ancestors.some(
    (level) =>
      level.segment === ORDER_DRAWER_SEGMENT && level.id === target?.orderKey,
  );
  const isOrderMissing = !isLoading && !isError && !hasData;
  // The lookup is disabled while the drawer closes, so only judge it while open.
  const isUnresolvable =
    open &&
    !pushed &&
    (!target ||
      (!isLoading &&
        (isError ||
          (isOrderMissing && !isOrderOwnedByAncestor) ||
          (hasData && !resolved))));

  useRouteNotFoundToast(
    isUnresolvable ? routeId : null,
    TRACKING_NOT_FOUND_MESSAGE,
    onNotFound,
  );

  const props = pushed ?? resolved;
  if (props) {
    return (
      <ReadyDrawerBase path={open ? levelPath : null}>
        <StackedTrackingDrawer
          open={open}
          onClose={onClose}
          onCloseAll={onCloseAll}
          zIndex={zIndex}
          depth={depth}
          {...props}
        />
      </ReadyDrawerBase>
    );
  }

  if (isUnresolvable) {
    return null;
  }

  return (
    <RoutedDrawerLoading
      open={open}
      onClose={onClose}
      onCloseAll={onCloseAll}
      zIndex={zIndex}
      depth={depth}
      label="Loading tracking details"
      paperSx={{ width: { xs: "100%", sm: 560 } }}
    />
  );
}
